const TXID_PATTERN = /^[0-9a-f]{64}$/i;

export interface MempoolTransaction {
  txid: string;
  fee?: number;
  vin: Array<{
    prevout?: {
      scriptpubkey_address?: string;
      value?: number;
    } | null;
  }>;
  vout: Array<{
    scriptpubkey_address?: string;
    scriptpubkey_type?: string;
    value: number;
  }>;
  status: {
    confirmed: boolean;
    block_time?: number;
  };
}

export interface InscriptionHistoryRecord {
  commitTxid: string;
  revealTxid: string;
  count: number;
  inscriptionIds: string[];
  createdAt: number | null;
  confirmed: boolean;
  /** True only after an accepted broadcast receipt or a successful transaction lookup. */
  broadcastVerified?: boolean;
}

export interface InscriptionHistoryPage {
  records: InscriptionHistoryRecord[];
  nextCursor: string | null;
  excludedRevealTxids: string[];
  excludedTransactionIds?: string[];
}

export interface InscriptionTransactionStatus {
  txid: string;
  found: true;
  confirmed: boolean;
  block_time?: number;
}

export type InscriptionTransactionStatuses = Record<string, InscriptionTransactionStatus | null>;

/** A provider's {confirmed:false} also describes unknown transactions; require identity proof. */
export function verifiedInscriptionTransactionStatus(txid: string, value: unknown): InscriptionTransactionStatus | null {
  if (typeof value !== "object" || value === null
    || !("found" in value) || value.found !== true
    || !("txid" in value) || value.txid !== txid
    || !("confirmed" in value) || typeof value.confirmed !== "boolean") return null;
  return {
    txid,
    found: true,
    confirmed: value.confirmed,
    ...("block_time" in value && typeof value.block_time === "number" && Number.isFinite(value.block_time)
      ? { block_time: value.block_time } : {}),
  };
}

export interface SpamTheChainCommitCandidate {
  commitTxid: string;
  count: number;
  revealOutputIndex: number;
  createdAt: number | null;
}

interface StoredInscriptionHistoryRecord extends InscriptionHistoryRecord {
  paymentAddress: string;
}

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const HISTORY_STORAGE_KEY = "spam-the-chain:inscription-history:v1";
const LEGACY_HISTORY_STORAGE_KEY = "yeetmint:inscription-history:v1";

export function buildInscriptionIds(revealTxid: string, count: number): string[] {
  if (!TXID_PATTERN.test(revealTxid)) throw new Error("Invalid reveal transaction ID");
  if (!Number.isSafeInteger(count) || count < 1 || count > 500) throw new Error("Invalid inscription count");
  return Array.from({ length: count }, (_, index) => `${revealTxid}i${index}`);
}

export function findSpamTheChainCommit(
  transaction: MempoolTransaction,
  paymentAddress: string,
  marketplaceFeeAddress: string,
  marketplaceFeeSats: number | readonly number[],
): SpamTheChainCommitCandidate | null {
  if (!TXID_PATTERN.test(transaction.txid) || !paymentAddress || !marketplaceFeeAddress) return null;
  if (!transaction.vin.some((input) => input.prevout?.scriptpubkey_address === paymentAddress)) return null;

  const feeOutputIndex = transaction.vout.findIndex((output) => output.scriptpubkey_address === marketplaceFeeAddress);
  if (feeOutputIndex < 1) return null;
  const feeValue = transaction.vout[feeOutputIndex]?.value ?? 0;
  if (!Number.isSafeInteger(feeValue)) return null;
  const feeRates: readonly number[] = (typeof marketplaceFeeSats === "number" ? [marketplaceFeeSats] : marketplaceFeeSats)
    .filter((rate) => Number.isSafeInteger(rate) && rate > 0);
  const matchedFeeRate = feeRates.find((rate) => feeValue % rate === 0);
  if (!matchedFeeRate) return null;
  const count = feeValue / matchedFeeRate;
  if (!Number.isSafeInteger(count) || count < 1 || count > 500) return null;

  const revealOutputs = transaction.vout.slice(0, feeOutputIndex).filter((output) => output.scriptpubkey_type === "v1_p2tr");
  if (revealOutputs.length === 0 || revealOutputs.reduce((total, output) => total + output.value, 0) < count * 330) return null;

  return {
    commitTxid: transaction.txid,
    count,
    revealOutputIndex: 0,
    createdAt: transaction.status.block_time ? transaction.status.block_time * 1000 : null,
  };
}

function parseStoredRecords(value: string | null): StoredInscriptionHistoryRecord[] {
  try {
    const parsed = JSON.parse(value ?? "[]") as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((record: unknown): record is StoredInscriptionHistoryRecord => (
      typeof record === "object" && record !== null
      && "paymentAddress" in record && typeof record.paymentAddress === "string"
      && "revealTxid" in record && typeof record.revealTxid === "string" && TXID_PATTERN.test(record.revealTxid)
      && "commitTxid" in record && typeof record.commitTxid === "string" && TXID_PATTERN.test(record.commitTxid)
      && "count" in record && Number.isSafeInteger(record.count) && Number(record.count) > 0
      && "inscriptionIds" in record && Array.isArray(record.inscriptionIds)
      && "createdAt" in record && (record.createdAt === null || typeof record.createdAt === "number")
      && "confirmed" in record && typeof record.confirmed === "boolean"
    ));
  } catch {
    return [];
  }
}

function readStoredRecords(storage: StorageLike): StoredInscriptionHistoryRecord[] {
  const legacy = parseStoredRecords(storage.getItem(LEGACY_HISTORY_STORAGE_KEY));
  const current = parseStoredRecords(storage.getItem(HISTORY_STORAGE_KEY));
  return [...new Map([...legacy, ...current].map((record) => [`${record.paymentAddress}:${record.revealTxid}`, record])).values()];
}

export function readLocalInscriptionHistory(paymentAddress: string, storage: StorageLike): InscriptionHistoryRecord[] {
  return readStoredRecords(storage)
    .filter((record) => record.paymentAddress === paymentAddress)
    .map((record) => ({
      commitTxid: record.commitTxid,
      revealTxid: record.revealTxid,
      count: record.count,
      inscriptionIds: record.inscriptionIds,
      createdAt: record.createdAt,
      confirmed: record.confirmed,
      ...(typeof record.broadcastVerified === "boolean" ? { broadcastVerified: record.broadcastVerified } : {}),
    }));
}

export function saveLocalInscriptionHistory(
  paymentAddress: string,
  record: InscriptionHistoryRecord,
  storage: StorageLike,
): void {
  try {
    const current = readStoredRecords(storage).filter((item) => item.revealTxid !== record.revealTxid);
    storage.setItem(HISTORY_STORAGE_KEY, JSON.stringify([{ ...record, paymentAddress }, ...current].slice(0, 250)));
  } catch {
    // On-chain discovery remains available when private browsing blocks local storage.
  }
}

export function removeLocalInscriptionHistory(
  paymentAddress: string,
  excludedTransactionIds: Iterable<string>,
  storage: StorageLike,
): void {
  try {
    const excluded = new Set(Array.from(excludedTransactionIds, (txid) => txid.toLowerCase()));
    const keep = (record: StoredInscriptionHistoryRecord) =>
      record.paymentAddress !== paymentAddress
      || (
        !excluded.has(record.revealTxid.toLowerCase())
        && !excluded.has(record.commitTxid.toLowerCase())
      );
    storage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(parseStoredRecords(storage.getItem(HISTORY_STORAGE_KEY)).filter(keep)));
    storage.setItem(LEGACY_HISTORY_STORAGE_KEY, JSON.stringify(parseStoredRecords(storage.getItem(LEGACY_HISTORY_STORAGE_KEY)).filter(keep)));
  } catch {
    // A stale local row is preferable to breaking history in restricted storage modes.
  }
}

export function mergeInscriptionHistory(
  current: InscriptionHistoryRecord[],
  incoming: InscriptionHistoryRecord[],
): InscriptionHistoryRecord[] {
  const records = new Map(current.map((record) => [record.revealTxid, record]));
  for (const record of incoming) {
    const previous = records.get(record.revealTxid);
    records.set(record.revealTxid, previous ? {
      ...previous,
      ...record,
      confirmed: previous.confirmed || record.confirmed,
      createdAt: record.createdAt ?? previous.createdAt,
    } : record);
  }
  return [...records.values()].sort((left, right) => (right.createdAt ?? Number.MAX_SAFE_INTEGER) - (left.createdAt ?? Number.MAX_SAFE_INTEGER));
}

export function excludeInscriptionHistory(records: InscriptionHistoryRecord[], excludedTransactionIds: Iterable<string>): InscriptionHistoryRecord[] {
  const excluded = new Set(Array.from(excludedTransactionIds, (txid) => txid.toLowerCase()));
  return records.filter((record) =>
    !excluded.has(record.revealTxid.toLowerCase())
    && !excluded.has(record.commitTxid.toLowerCase()),
  );
}

export function inscriptionDraftsAreHistoryEligible(drafts: readonly { delegateId?: string }[]): boolean {
  return drafts.every((draft) => typeof draft.delegateId !== "string" || draft.delegateId.trim() === "");
}

export function inscriptionHistoryStatus(record: InscriptionHistoryRecord): "CONFIRMED" | "PENDING" | "STATUS UNVERIFIED" {
  return record.confirmed ? "CONFIRMED" : record.broadcastVerified === true ? "PENDING" : "STATUS UNVERIFIED";
}

export function applyInscriptionTransactionStatuses(
  records: InscriptionHistoryRecord[],
  statuses: InscriptionTransactionStatuses,
): InscriptionHistoryRecord[] {
  return records.map((record) => {
    const status = verifiedInscriptionTransactionStatus(record.revealTxid, statuses[record.revealTxid]);
    if (!status) return Object.hasOwn(statuses, record.revealTxid) && !record.confirmed
      ? { ...record, broadcastVerified: false }
      : record;
    return {
      ...record,
      confirmed: record.confirmed || status.confirmed,
      broadcastVerified: true,
      createdAt: status.block_time ? status.block_time * 1000 : record.createdAt,
    };
  });
}
